import { BETA_EVENTS, betaEventPayload } from './betaPayload.js';
// Public GA4 identifier, not a credential. One manually controlled SPA stream.
export const MEASUREMENT_ID = 'G-LTPT89Q54T';
export const CONSENT_KEY = 'btl_analytics_consent_v1';
// Advertising is a separate, explicit choice; legacy analytics opt-ins do not grant it.
export function advertisingAllowed(consent, now = Date.now()) {
    return consent?.choice === 'granted' && consent.advertising === true &&
        Number.isFinite(consent.at) && consent.at <= now && now - consent.at < MAX_AGE;
}

export function adLandingQuery(href) {
    try {
        const url = new URL(href);
        if (url.protocol !== 'https:' || !['daniellenglish.com', 'www.daniellenglish.com'].includes(url.hostname)) return '';
        const clean = new URLSearchParams();
        for (const key of ['gclid', 'gbraid', 'wbraid']) {
            const values = url.searchParams.getAll(key);
            if (values.length === 1 && /^[A-Za-z0-9_-]{10,256}$/.test(values[0])) clean.set(key, values[0]);
        }
        return clean.toString();
    } catch { return ''; }
}
const MAX_AGE = 180 * 24 * 60 * 60 * 1000;
const ORIGIN = 'https://daniellenglish.com';
const STATIC_PATHS = new Set(['/', '/home', '/dashboard', '/login', '/legal', '/faq',
    '/personal-statement', '/personal-statement/request', '/booking', '/cart',
    '/checkout', '/checkout/success', '/checkout/cancel', '/account', '/terminology', '/study-partner']);
const PACK_ROUTES = new Set(['preview', 'set-text', 'skills-workshop', 'literature-study',
    'poetry-anthology', 'study-partner', 'pack-payment-choice', 'pack-payment-payme']);
const METHODS = new Set(['email', 'google', 'card', 'apple', 'payme', 'fps', 'bank_transfer', 'stripe', 'manual']);
const EVENTS = new Set(['sign_up', 'login', 'view_item', 'view_item_list', 'view_cart',
    'add_to_cart', 'remove_from_cart', 'begin_checkout', 'add_payment_info',
    'manual_payment_submitted', 'generate_lead', 'purchase', 'contact_click', 'scroll',
    'ai_demo_open', 'ai_partner_demo', 'ai_feedback_demo', 'ai_demo_lesson',
    'terminology_preview', 'terminology_open', 'terminology_search', 'terminology_category',
    'hero_lesson', 'parents_preview', ...BETA_EVENTS]);

// Dynamic route segments are only sent when they match the public pack catalogue.
// All queries/fragments (including Stripe sessions, emails, form text) are removed.
export function safePage(pathname, packs = []) {
    if (STATIC_PATHS.has(pathname)) return pathname === '/' ? '/home' : pathname;
    const parts = pathname.split('/');
    if (parts.length === 3 && PACK_ROUTES.has(parts[1]) &&
        packs.some(p => p.id === parts[2]) && /^[a-zA-Z0-9_-]{1,100}$/.test(parts[2])) return pathname;
    return null;
}

export function readConsent(storage, now = Date.now()) {
    try {
        const c = JSON.parse(storage.getItem(CONSENT_KEY));
        if (c && ['granted', 'denied'].includes(c.choice) && Number.isFinite(c.at) &&
            c.at <= now && now - c.at < MAX_AGE) return c;
    } catch { /* Storage may be disabled. */ }
    return null;
}

export function itemsFor(packs) {
    return (packs || []).filter(p => p && /^[a-zA-Z0-9_-]{1,100}$/.test(p.id || ''))
        .map(p => ({ item_id: p.id, quantity: 1,
            ...(Number.isFinite(p.price) && p.price >= 0 ? { price: p.price } : {}) }));
}

// Strict allowlist: never forward a caller's object, title, URL, UID or form data.
export function eventPayload(name, input = {}) {
    if (!EVENTS.has(name)) return null;
    if (BETA_EVENTS.includes(name)) return betaEventPayload(name, input);
    const out = {};
    if (['email', 'instagram'].includes(input.contact_type)) out.contact_type = input.contact_type;
    if (name === 'scroll' && input.percent_scrolled === 90) out.percent_scrolled = 90;
    if (METHODS.has(input.method)) out.method = input.method;
    if (METHODS.has(input.payment_type)) out.payment_type = input.payment_type;
    if (['booking', 'personal_statement_review', 'personal_statement_consultation'].includes(input.lead_type)) out.lead_type = input.lead_type;
    if (Array.isArray(input.items)) out.items = input.items.filter(i => /^[a-zA-Z0-9_-]{1,100}$/.test(i.item_id || '')).slice(0,200)
        .map(i => ({ item_id: i.item_id, quantity: 1, ...(Number.isFinite(i.price) && i.price >= 0 ? { price: i.price } : {}) }));
    if (Number.isFinite(input.value) && input.value >= 0 && /^[A-Z]{3}$/.test(input.currency || '')) {
        out.value = input.value;
        out.currency = input.currency;
    }
    if (name === 'purchase') {
        if (!/^btl_[a-f0-9]{64}$/.test(input.transaction_id || '') || out.value === undefined) return null;
        out.transaction_id = input.transaction_id;
    }
    return out;
}

export function purchasePayload(txn, lines, id) {
    // Existing BTL schema stores the minor-unit NAME, not a decimal exponent.
    const units = { HKD: 'cents', USD: 'cents', SGD: 'cents', GBP: 'pence' };
    if (txn.status !== 'paid' || !Number.isFinite(txn.totalAmount) || txn.totalAmount < 0 ||
        !units[txn.currency] || txn.minorUnits !== units[txn.currency] ||
        !Array.isArray(lines) || !lines.length || lines.some(l => !Number.isFinite(l.finalAmount) || l.finalAmount < 0 || !/^[a-zA-Z0-9_-]{1,100}$/.test(l.packId || ''))) return null;
    return eventPayload('purchase', { transaction_id: id, currency: txn.currency,
        value: txn.totalAmount / 100, payment_type: txn.paymentMethod || txn.source,
        items: lines.map(l => ({ item_id: l.packId, quantity: 1, price: l.finalAmount / 100 })) });
}

export function createAnalytics(win, enabled) {
    let consent = null;
    try { consent = readConsent(win?.localStorage); } catch { /* Storage getter can throw. */ }
    let permitted = false, loaded = false, lastPage = null, page = null, referrer = '';
    let adLandingMeasured = false;
    const campaign = {};
    try {
        const params = new URL(win.location.href).searchParams;
        for (const key of ['source', 'medium', 'campaign']) {
            const value = params.get('utm_' + key);
            if (/^[a-zA-Z0-9_-]{1,64}$/.test(value || '')) campaign[key === 'campaign' ? 'campaign_name' : 'campaign_' + key] = value;
        }
    } catch { /* No campaign. */ }
    const seen = new Set();
    const command = (...args) => {
        win.dataLayer = win.dataLayer || [];
        // gtag consumes Arguments objects, not arrays.
        function gtag() { win.dataLayer.push(arguments); }
        gtag(...args);
    };
    const active = () => enabled && permitted && !!page && consent?.choice === 'granted' && Date.now() - consent.at < MAX_AGE;
    const consentState = () => ({
        analytics_storage: consent?.choice === 'granted' ? 'granted' : 'denied',
        ad_storage: advertisingAllowed(consent) ? 'granted' : 'denied',
        ad_user_data: advertisingAllowed(consent) ? 'granted' : 'denied',
        ad_personalization: 'denied'
    });
    function clearCookies(advertisingOnly = false) {
        for (const cookie of win.document.cookie.split(';')) {
            const name = cookie.trim().split('=')[0];
            if (!(advertisingOnly ? /^_gcl(?:_|$)/ : /^(?:_ga|_gcl)(?:_|$)/).test(name)) continue;
            for (const domain of ['', '; domain=' + win.location.hostname, '; domain=.daniellenglish.com']) {
                win.document.cookie = name + '=; Max-Age=0; path=/' + domain;
            }
        }
    }
    function sync() {
        if (!win) return;
        win['ga-disable-' + MEASUREMENT_ID] = !active();
        if (!active()) return;
        if (!loaded) {
            loaded = true;
            command('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
            command('set', 'ads_data_redaction', true);
            command('set', 'url_passthrough', false);
            command('set', { page_location: ORIGIN + page, page_referrer: referrer, page_title: 'BTL ' + page });
            command('consent', 'update', consentState());
            command('js', new Date());
            command('config', MEASUREMENT_ID, { send_page_view: false, allow_google_signals: false,
                allow_ad_personalization_signals: false, cookie_expires: MAX_AGE / 1000, ...campaign });
            const script = win.document.createElement('script');
            script.async = true;
            script.src = 'https://www.googletagmanager.com/gtag/js?id=' + MEASUREMENT_ID;
            win.document.head.appendChild(script);
        }
        if (lastPage !== page) {
            // Let GA4 read the consented ad landing identifier once. Its own
            // first-party attribution handles later pages/checkout returns.
            // Never persist a second click-ID store or forward the whole URL.
            const adQuery = advertisingAllowed(consent) && !adLandingMeasured ? adLandingQuery(win.location.href) : '';
            if (advertisingAllowed(consent)) adLandingMeasured = true;
            const measuredLocation = ORIGIN + page + (adQuery ? '?' + adQuery : '');
            command('set', { page_location: measuredLocation, page_referrer: referrer, page_title: 'BTL ' + page });
            command('event', 'page_view', { send_to: MEASUREMENT_ID, page_location: measuredLocation,
                page_referrer: referrer, page_title: 'BTL ' + page });
            // Other events cannot accidentally inherit landing query parameters.
            command('set', { page_location: ORIGIN + page });
            lastPage = page;
            referrer = ORIGIN + page;
        }
    }
    try {
        const ref = new URL(win.document.referrer);
        // External domain only. No third-party paths, query strings or fragments.
        if (ref.origin !== ORIGIN && ref.protocol === 'https:' && !ref.hostname.endsWith('stripe.com')) referrer = ref.origin + '/';
    } catch { /* No referrer. */ }
    return {
        consent: () => consent,
        active,
        refreshConsent() {
            const previouslyAdvertising = advertisingAllowed(consent);
            try { consent = readConsent(win.localStorage); } catch { consent = null; }
            if (loaded) {
                command('set', { page_location: page ? ORIGIN + page : ORIGIN + '/home' });
                command('consent', 'update', consentState());
            }
            if (consent?.choice !== 'granted') clearCookies();
            else if (!advertisingAllowed(consent)) clearCookies(true);
            if (!previouslyAdvertising && advertisingAllowed(consent)) lastPage = null;
            if (consent?.choice !== 'granted') lastPage = null;
            sync();
        },
        setContext(path, allowed) { page = path; permitted = allowed; sync(); },
        choose(choice, advertising = false) {
            if (!['granted', 'denied'].includes(choice)) return;
            const previouslyAdvertising = advertisingAllowed(consent);
            consent = { choice, at: consent?.choice === choice ? consent.at : Date.now(), advertising: choice === 'granted' && advertising === true };
            try { win.localStorage.setItem(CONSENT_KEY, JSON.stringify(consent)); } catch { /* Session only. */ }
            if (loaded) {
                command('set', { page_location: page ? ORIGIN + page : ORIGIN + '/home' });
                command('consent', 'update', consentState());
            }
            if (!advertisingAllowed(consent)) clearCookies(true);
            if (!previouslyAdvertising && advertisingAllowed(consent)) lastPage = null;
            if (choice === 'denied') {
                lastPage = null;
                seen.clear();
                win['ga-disable-' + MEASUREMENT_ID] = true;
                // Revoke locally even if Google is blocked or fails to load.
                clearCookies();
            }
            sync();
        },
        event(name, input, onceKey) {
            if (!active()) return false;
            const payload = eventPayload(name, input);
            if (!payload || (onceKey && seen.has(onceKey))) return false;
            if (onceKey) seen.add(onceKey);
            command('event', name, { ...payload, send_to: MEASUREMENT_ID });
            return true;
        }
    };
}

const browser = typeof window === 'undefined' ? null : window;
export const analytics = browser ? createAnalytics(browser,
    import.meta.env?.PROD && import.meta.env?.VITE_AUTH_MODE === 'firebase' &&
    ['daniellenglish.com', 'www.daniellenglish.com'].includes(browser.location.hostname)) : null;
// Analytics must never make a successful business operation fail.
export function track(name, input, onceKey) {
    try { return analytics?.event(name, input, onceKey) || false; } catch { return false; }
}
export function trackItems(name, packs, extra = {}, onceKey) {
    const items = itemsFor(packs);
    return track(name, { items, currency: 'HKD', value: items.reduce((v,i) => v + (i.price || 0), 0), ...extra }, onceKey);
}

export async function trackAuthentication(user, name, method) {
    try {
        const token = await user.getIdTokenResult();
        if (token.claims?.role === 'admin') return;
        analytics?.setContext(safePage(window.location.pathname), true);
        track(name, { method });
    } catch { /* Authentication must succeed independently of analytics. */ }
}
